import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsSeoRule, CmsSeoRuleInput } from "services/cms-seo/type"

import { Field, FieldRow, ghostButtonClass, inputClass, modalBoxClass, primaryButtonClass, ToggleRow} from "./CmsUi"

export type SeoRuleFormState = { kind: "none" } | { kind: "create" } | { kind: "edit"; record: CmsSeoRule }

const EMPTY: CmsSeoRuleInput = {
  priority: 50,
  name: "",
  url_pattern: "",
  title_template: "",
  description_template: "",
  robots_index: true,
  active: true
}

const toInput = (record: CmsSeoRule): CmsSeoRuleInput => ({
  priority: record.priority,
  name: record.name,
  url_pattern: record.url_pattern,
  title_template: record.title_template ?? "",
  description_template: record.description_template ?? "",
  robots_index: record.robots_index,
  active: record.active
})

interface SeoRuleModalProps {
  state: SeoRuleFormState
  onClose: () => void
  onSaved: () => void
}

export const SeoRuleModal: FC<SeoRuleModalProps> = ({ state, onClose, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [form, setForm] = useState<CmsSeoRuleInput>(EMPTY)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (state.kind === "edit") setForm(toInput(state.record))
    if (state.kind === "create") setForm(EMPTY)
  }, [state])

  const set = <K extends keyof CmsSeoRuleInput>(key: K, value: CmsSeoRuleInput[K]) => setForm((prev) => ({ ...prev, [key]: value }))

  const submit = async () => {
    if (!form.name.trim() || !form.url_pattern.trim()) {
      toast.error(t("cmsSeo.ruleMissingFields", { defaultValue: "A rule needs a name and a URL pattern" }) as string)
      return
    }
    setSaving(true)
    const saved = state.kind === "edit" ? await cmsSeoService.updateSeoRule(state.record.id, form) : await cmsSeoService.createSeoRule(form)
    setSaving(false)
    if (saved) {
      toast.success(t("cmsSeo.ruleSaved", { defaultValue: "Rule saved" }) as string)
      onSaved()
    }
  }

  return (
    <Modal open={state.kind !== "none"} handler={onClose}>
      <div className={`${modalBoxClass} max-w-2xl`}>
        <h3 className="mb-4 text-lg font-semibold">
          {state.kind === "edit"
            ? t("cmsSeo.editRule", { defaultValue: "Edit SEO rule" })
            : t("cmsSeo.createRule", { defaultValue: "Create SEO rule" })}
        </h3>

        <FieldRow>
          <Field label={t("cmsSeo.rule.name", { defaultValue: "Rule name" })}>
            <input className={inputClass} value={form.name} onChange={(event) => set("name", event.target.value)} />
          </Field>
          <Field
            label={t("cmsSeo.rule.priority", { defaultValue: "Priority" })}
            className="min-w-[120px] flex-none"
            hint={t("cmsSeo.rule.priorityHint", { defaultValue: "Lowest number wins" })}
          >
            <input
              type="number"
              className={inputClass}
              value={form.priority}
              onChange={(event) => set("priority", Number(event.target.value))}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field
            label={t("cmsSeo.rule.pattern", { defaultValue: "URL pattern" })}
            hint={t("cmsSeo.rule.patternHint", { defaultValue: "Named segments use a colon, e.g. /casino/game/:game" })}
          >
            <input
              className={`${inputClass} font-mono`}
              value={form.url_pattern}
              onChange={(event) => set("url_pattern", event.target.value)}
              placeholder="/sports/:sport/:country/:league"
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field
            label={t("cmsSeo.rule.titleTemplate", { defaultValue: "Title template" })}
            hint={t("cmsSeo.rule.templateHint", {
              defaultValue:
                'Placeholders wrap in double braces: {{league}}, {{brand}}, {{year}}. A captured segment reads as a name — :sport on /sportsbook/soccer-1 gives "Soccer", not "soccer-1".'
            })}
          >
            <input
              className={`${inputClass} font-mono`}
              value={form.title_template ?? ""}
              onChange={(event) => set("title_template", event.target.value)}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label={t("cmsSeo.rule.descriptionTemplate", { defaultValue: "Description template" })}>
            <textarea
              className={`${inputClass} font-mono`}
              rows={2}
              value={form.description_template ?? ""}
              onChange={(event) => set("description_template", event.target.value)}
            />
          </Field>
        </FieldRow>

        <ToggleRow
          title={t("cmsSeo.rule.index", { defaultValue: "Index matching URLs" })}
          description={t("cmsSeo.rule.indexHint", { defaultValue: "Off emits noindex on every URL the pattern matches" })}
          checked={form.robots_index}
          onChange={() => set("robots_index", !form.robots_index)}
        />
        <ToggleRow
          title={t("cmsSeo.rule.active", { defaultValue: "Active" })}
          description={t("cmsSeo.rule.activeHint", { defaultValue: "Paused rules stay listed but stop applying" })}
          checked={form.active}
          onChange={() => set("active", !form.active)}
        />

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={saving} onClick={submit}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
